import { useEffect } from 'react';
import key from 'weak-key';
import { CarouselSSRWrapper, Dialogue } from '@geberit/gdds';

// styles
import styles from './image-gallery.module.scss';

// types
import type { OverlayNordicsProps, OverlayNordics } from '../types';

// components
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { Image } from 'components/image/image';
import { CmsLink } from 'components/Link/CmsLink';

// utils
import { useIsMobile } from 'components/App/SizeProvider';
import { previewId } from 'utils/preview-id';
import { usePreviewIdBuilder } from 'utils/preview-id-builder';

export function Overlay({ items, onClose, index }: Readonly<OverlayNordicsProps>) {
  const isMobile = useIsMobile();
  const previewIdBuilder = usePreviewIdBuilder();
  const overlaySize = isMobile ? 'xl' : 'l';

  useEffect(() => {
    const handleKeyEvents = (e: KeyboardEvent) => {
      switch (e?.code) {
        case 'ArrowUp':
          e.preventDefault();
          break;
        case 'ArrowDown':
          e.preventDefault();
          break;
        default:
          return e;
      }
      e.preventDefault();
      return null;
    };

    window.addEventListener('keydown', handleKeyEvents);

    return () => {
      window.removeEventListener('keydown', handleKeyEvents);
    };
  });

  return (
    <div className={styles.overlay}>
      <Dialogue
        size={overlaySize}
        fullscreen
        type="custom"
        noFooterButton
        closeDialogue={onClose}
        body={
          <div
            data-preview-id={previewIdBuilder('#st_imageGallery')}
            className={styles.overlayWrapper}
          >
            <CarouselSSRWrapper
              responsiveLayout={{
                small: { slides: 1, slidesToMove: 1 },
                medium: { slides: 1, slidesToMove: 1 },
                large: { slides: 1, slidesToMove: 1 },
              }}
              startIndex={index}
              hideButtons
              supportMouse
              customButtonLeft={isMobile}
              customButtonRight={isMobile}
            >
              {items.map((imageItem, itemIndex) => {
                const image =
                  (isMobile && imageItem.pictureObjectMobile) ||
                  imageItem.pictureObject ||
                  imageItem.picture;
                const ratio =
                  (isMobile && imageItem.pictureObject?.aspectRatio?.replace('_', ':')) ||
                  imageItem.pictureObject?.aspectRatio?.replace('_', ':');
                const overlay = imageItem.overlay as OverlayNordics;

                return (
                  <div
                    {...previewId(`#${itemIndex}`)}
                    key={key(imageItem)}
                    className={styles.listItem}
                  >
                    <Image
                      data={image}
                      alt={imageItem?.pictureAlt}
                      title={imageItem?.pictureAlt}
                      src={image?.url}
                      format={image?.format}
                      aspectRatio={ratio}
                    />
                    {(overlay?.text || overlay?.link) && (
                      <div className={styles.overlayContent}>
                        {overlay.text && (
                          <InnerHtml
                            previewId="#st_overlayText"
                            as="div"
                            content={overlay.text}
                            isGdds
                            className={styles.overlayText}
                          />
                        )}
                        {overlay.link && <CmsLink stylingType="primary" link={overlay.link} />}
                      </div>
                    )}
                  </div>
                );
              })}
            </CarouselSSRWrapper>
          </div>
        }
      />
    </div>
  );
}
